import { Component } from '@theme/component';
import { scrollIntoView } from '@theme/scrolling';
import { debounce } from '@theme/utilities';

/**
 * apc: centres the selected sibling swatch in its mobile scroll row.
 *
 * The sibling row scrolls horizontally under 750px (see `blocks/product-siblings.liquid`). Without
 * this, the row always starts at the first swatch, so on a colour family large enough to overflow
 * the shopper can land on a PDP whose selected colour sits off to the right, out of view — the block
 * then reads as though a different colour is active.
 *
 * The edge fading is not handled here: the row is a `<scroll-hint>`, which maintains its own mask
 * from the scroll position. Moving the scroll offset is enough to keep the fades correct.
 *
 * Hosted as an empty `<apc-sibling-scroll>` element inside the block's scroll-row branch, so a
 * section reload or re-render reconnects it and re-centres on its own — no `shopify:section:load`
 * listener needed. Scoped to its own block, so pages with several sibling blocks centre each row
 * independently.
 *
 * Uses the theme's `scrollIntoView` with an explicit `ancestor` rather than the native DOM method.
 * Native `scrollIntoView` walks every scrollable ancestor, so on a PDP it would also scroll the
 * *page* to bring the row into view, jumping the shopper past the gallery on load. Passing the
 * ancestor makes it compute the offset and scroll only that container. Same call the media gallery
 * uses to centre its selected thumbnail (`slideshow.js` #centerSelectedThumbnail).
 */

/** Matches the `max-width: 749px` scroll-row breakpoint in `blocks/product-siblings.liquid`. */
const MOBILE_BREAKPOINT = 749;

/**
 * Centres the checked swatch within its scroll container.
 * @param {HTMLElement} row - The `.variant-option--buttons` scroll container.
 * @param {ScrollBehavior} behavior - The scroll behavior.
 */
function centerSelected(row, behavior) {
  const selected = row.querySelector('[data-option-checked]');
  if (!(selected instanceof HTMLElement)) return;

  // Nothing to centre when the row fits. Bailing rather than scrolling a no-op keeps this from
  // fighting the browser's scroll restoration on a back-navigation.
  if (row.scrollWidth <= row.clientWidth) return;

  // block is 'start', not 'nearest': the theme's scrollIntoView only handles start/center/end and
  // silently no-ops otherwise. Moot either way — it only touches the vertical axis when
  // scrollHeight > clientHeight, and this row never scrolls vertically.
  scrollIntoView(selected, { ancestor: row, behavior, block: 'start', inline: 'center' });
}

class ApcSiblingScroll extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // 'instant' on first paint (and on re-render reconnects): the swatch should already be centred
    // when the shopper first sees the row, not animate into place from the left.
    this.#centerRows('instant');

    // A variant change can morph the row markup in place without reconnecting this element
    // (resetting scroll position), so re-centre on the event too. Smooth: the row is already on
    // screen and the movement answers the shopper's own action.
    document.addEventListener('variant:update', this.#onVariantUpdate, { signal });

    // A resize can cross the breakpoint or change how many swatches fit, invalidating the centring.
    // Also covers orientation changes. Debounced: each run does layout reads and a scroll write,
    // which has no business firing on every resize frame.
    window.addEventListener(
      'resize',
      debounce(() => this.#centerRows('instant'), 150),
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  #onVariantUpdate = () => this.#centerRows('smooth');

  /**
   * Centres the selected swatch in every sibling row of this element's block.
   * @param {ScrollBehavior} behavior - The scroll behavior.
   */
  #centerRows(behavior) {
    // Desktop wraps instead of scrolling, so there is no overflow and nothing to centre.
    if (window.innerWidth > MOBILE_BREAKPOINT) return;

    const block = this.closest('.product-siblings-block');
    if (!block) return;

    for (const row of block.querySelectorAll('.variant-option--buttons')) {
      if (row instanceof HTMLElement) centerSelected(row, behavior);
    }
  }
}

if (!customElements.get('apc-sibling-scroll')) {
  customElements.define('apc-sibling-scroll', ApcSiblingScroll);
}
